import { useMemo } from 'react';

import { fmt } from '@dcc-erp/shared';
import type { ChartOfAccount } from '@dcc-erp/shared';

import { PALETTE, type Branch } from './pc-helpers';

import type { CategorySummaryRow } from '../../routes/pettycash.logic';

export interface PCCategoryProps {
  rows: readonly CategorySummaryRow[];
  topBranches: readonly Branch[];
  coa: ChartOfAccount[];
  total: number;
  isPending: boolean;
}

export function PCCategory({ rows, topBranches, coa, total, isPending }: PCCategoryProps) {
  const coaName = useMemo(() => new Map(coa.map((a) => [a.code, a.nameTh])), [coa]);
  const max = Math.max(...rows.map((r) => r.amount), 1);
  const totalCount = rows.reduce((a, r) => a + r.count, 0);
  const totalAmount = rows.reduce((a, r) => a + r.amount, 0);

  return (
    <div className="grid-2">
      {/* LEFT — category breakdown table */}
      <div className="card">
        <div className="card-head">
          <div>
            <h3>ค่าใช้จ่ายเงินสดย่อยตามประเภท</h3>
            <div className="sub">รวม {rows.length} ประเภท</div>
          </div>
        </div>
        <div className="card-body flush">
          {isPending ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <div className="muted" style={{ fontSize: 15 }}>
                กำลังโหลด…
              </div>
            </div>
          ) : rows.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <div className="muted" style={{ fontSize: 15 }}>
                ยังไม่มีรายการเงินสดย่อยในงวดนี้
              </div>
            </div>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>รหัส</th>
                  <th>ประเภทค่าใช้จ่าย</th>
                  <th style={{ width: 200 }}>สัดส่วน</th>
                  <th className="num">จำนวนรายการ</th>
                  <th className="num">มูลค่ารวม</th>
                  <th className="num">เฉลี่ย/ใบ</th>
                  <th className="num">% ของรวม</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const color = PALETTE[i % PALETTE.length];
                  return (
                    <tr key={r.categoryCode}>
                      <td className="code">{r.categoryCode}</td>
                      <td>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          <span
                            style={{
                              width: 9,
                              height: 9,
                              borderRadius: 2,
                              background: color,
                              flexShrink: 0,
                            }}
                          />
                          {coaName.get(r.categoryCode) ?? r.categoryCode}
                        </span>
                      </td>
                      <td>
                        <div
                          style={{
                            height: 6,
                            borderRadius: 3,
                            background: '#f0f0f3',
                            overflow: 'hidden',
                          }}
                        >
                          <div
                            style={{
                              width: `${(r.amount / max) * 100}%`,
                              height: '100%',
                              background: color,
                            }}
                          />
                        </div>
                      </td>
                      <td className="num tnum">{fmt(r.count)}</td>
                      <td className="num tnum">
                        <b>{fmt(r.amount)}</b>
                      </td>
                      <td className="num tnum muted">{fmt(Math.round(r.amount / r.count))}</td>
                      <td className="num">
                        {total > 0 ? ((r.amount / total) * 100).toFixed(1) : '0.0'}%
                      </td>
                    </tr>
                  );
                })}
                <tr className="total">
                  <td colSpan={3}>รวม {rows.length} ประเภท</td>
                  <td className="num tnum">{fmt(totalCount)}</td>
                  <td className="num tnum">{fmt(totalAmount)}</td>
                  <td colSpan={2} />
                </tr>
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* RIGHT — top-10 branches */}
      <div className="card">
        <div className="card-head">
          <h3>10 สาขาที่ใช้จ่ายมากที่สุด</h3>
        </div>
        <div className="card-body flush">
          {isPending ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <div className="muted" style={{ fontSize: 15 }}>
                กำลังโหลด…
              </div>
            </div>
          ) : topBranches.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <div className="muted" style={{ fontSize: 15 }}>
                ยังไม่มีรายการเงินสดย่อยในงวดนี้
              </div>
            </div>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ width: 50 }}>อันดับ</th>
                  <th>สาขา</th>
                  <th className="num">รายการ</th>
                  <th className="num">ยอดเบิก</th>
                </tr>
              </thead>
              <tbody>
                {topBranches.map((b, i) => (
                  <tr key={b.id}>
                    <td>
                      <span
                        style={{
                          display: 'inline-block',
                          width: 22,
                          height: 22,
                          borderRadius: '50%',
                          background: i < 3 ? 'var(--brand)' : '#ececef',
                          color: i < 3 ? 'white' : '#52525b',
                          textAlign: 'center',
                          lineHeight: '22px',
                          fontSize: 14,
                          fontWeight: 600,
                        }}
                      >
                        {i + 1}
                      </span>
                    </td>
                    <td>
                      {b.id} <span className="muted">— {b.name}</span>
                    </td>
                    <td className="num">{b.entries}</td>
                    <td className="num tnum">
                      <b>{fmt(b.total)}</b>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
